import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react'
import { StepperFormValues } from '../utils/types'

interface UseStepperProps {
  totalSteps: number
  initialStep?: number
  onFinish?: () => void
  initialValues?: Partial<StepperFormValues>
}

interface UseStepperReturn {
  currentStep: number
  isFirstStep: boolean
  isLastStep: boolean
  canGoNext: boolean
  canGoPrevious: boolean
  goToNext: <T>(values: T) => void
  goToPrevious: () => void
  goToStep: (step: number) => void
  reset: () => void
  maxSteps: number
  formValues: StepperFormValues
}

const useStepperBase = ({
  totalSteps,
  initialStep = 0,
  onFinish,
  initialValues,
}: UseStepperProps): UseStepperReturn => {
  const [currentStep, setCurrentStep] = useState(initialStep)
  const [formValues, setFormValues] = useState(initialValues ?? {})

  const isFirstStep = currentStep === 0
  const isLastStep = currentStep === totalSteps - 1
  const canGoNext = !isLastStep
  const canGoPrevious = !isFirstStep
  const maxSteps = totalSteps

  const goToNext = useCallback(
    <T,>(values: T) => {
      setFormValues({ ...formValues, ...values })
      if (canGoNext) {
        setCurrentStep((prev) => prev + 1)
      } else {
        onFinish?.()
      }
    },
    [canGoNext, onFinish, setFormValues, formValues],
  )

  const goToPrevious = useCallback(() => {
    if (canGoPrevious) {
      setCurrentStep((prev) => prev - 1)
    }
  }, [canGoPrevious])

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < totalSteps) {
        setCurrentStep(step)
      }
    },
    [totalSteps],
  )

  const reset = useCallback(() => {
    setCurrentStep(initialStep)
  }, [initialStep])

  return {
    currentStep,
    isFirstStep,
    isLastStep,
    canGoNext,
    canGoPrevious,
    goToNext,
    goToPrevious,
    goToStep,
    reset,
    maxSteps,
    formValues: formValues as StepperFormValues,
  }
}

interface ChecklistStepperProviderProps {
  totalSteps: number
  initialStep?: number
  children: ReactNode
  onFinish?: () => void
}

const ChecklistStepperContext = createContext<UseStepperReturn | undefined>(
  undefined,
)

export const ChecklistStepperProvider = ({
  totalSteps,
  initialStep = 0,
  onFinish,
  children,
}: ChecklistStepperProviderProps) => {
  const stepper = useStepperBase({ totalSteps, initialStep, onFinish })
  return (
    <ChecklistStepperContext.Provider value={stepper}>
      {children}
    </ChecklistStepperContext.Provider>
  )
}

export const useChecklistStepper = () => {
  const context = useContext(ChecklistStepperContext)
  if (!context) {
    throw new Error(
      'useChecklistStepper must be used within a ChecklistStepperProvider',
    )
  }
  return context
}
